import { useCallback, useMemo } from "react";
import { Grid2, Typography } from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Elements } from "@stripe/react-stripe-js";
import Breadcrumbs from "../../../../components/Breadcrumbs";
import { useTranslate } from "../../../../hooks/useTranslate";
import { useStudents } from "../../../../context/students";
import axiosManager from "../../../../services/axiosManager";
import Preloader from "../../../../components/Preloader";
import CheckoutForm from "../../../applicant/TuitionBilling/components/CheckoutForm";
import CheckoutStatus from "../../../applicant/TuitionBilling/components/CheckoutStatus";
import useStatuses from "../../../../context/status/useStatuses";
import { stripeAppearance, stripeFonts, stripePromise } from "../../../../services/stripeConfig";

export const InvoicePay = () => {
    const { t } = useTranslate();
    const { invoiceId } = useParams();
    const { generalStatuses } = useStatuses();
    const navigate = useNavigate();

    const [searchParams] = useSearchParams();
    const hasPaymentComplete = searchParams.has("payment-complete");
    const isPaymentProcessing = generalStatuses?.tuitionPaymentStatus === "processing";

    const { activeStudent, activeStudentId } = useStudents();
    const studentFullName = `${activeStudent.forename} ${activeStudent.surname}`;

    const { isLoading, data: payment } = useQuery({
        queryKey: [`Payment/${activeStudentId}/invoice/${invoiceId}/pay`],
        queryFn: () => axiosManager.post$(`/parent/api/Payment/${activeStudentId}/invoice/${invoiceId}/pay`).then((response) => response?.data),
        retry: 0,
        cacheTime: 0,
        enabled: !!activeStudentId,
    });

    const stripeOptions = useMemo(() => ({
        clientSecret: payment?.clientSecret,
        appearance: stripeAppearance,
        fonts: stripeFonts,
        loader: "auto",
    }), [payment?.clientSecret]);

    const redirect = useCallback(() => {
        setTimeout(() => {
            navigate(`/finance/payments/${activeStudentId}`);
        }, 3000);
    }, [navigate]);

    return (
        <div className="page-content">
            <div className="student-head-section" />
            <Grid2 container spacing={2} className="min-head-section-height">
                <Grid2 size={{ xs: 12, md: 7, lg: 8 }} className="text-white">
                    <Breadcrumbs
                        breadcrumbs={[
                            { label: t("navigation.home"), href: `/home/${activeStudentId}` },
                            { label: t("finance.title"), href: `/finance/${activeStudentId}` },
                            { label: t("payments.title"), href: `/finance/payments/${activeStudentId}` },
                            { label: t("payInvoice.title"), current: true },
                        ]}
                    />
                    <Typography variant="h1" className="text-white">
                        {t("payInvoice.title")}
                    </Typography>
                    <Typography variant="body1" className="mt-4 text-white" component="div">
                        {t("payInvoice.subtitle")}
                    </Typography>
                </Grid2>
            </Grid2>
            <div className="relative z-1">
                <Preloader isLoading={isLoading}>
                    {hasPaymentComplete && (
                        <Elements options={stripeOptions} stripe={stripePromise}>
                            <CheckoutStatus redirect={redirect} />
                        </Elements>
                    )}
                    {isPaymentProcessing && (
                        <Elements options={stripeOptions} stripe={stripePromise}>
                            <CheckoutStatus redirect={redirect} isProcessing />
                        </Elements>
                    )}
                    {(!hasPaymentComplete && !isPaymentProcessing && payment) && (
                        <Elements
                            options={stripeOptions}
                            stripe={stripePromise}
                        >
                            <CheckoutForm
                                studentFullName={studentFullName}
                                invoice={payment?.invoice}
                                isInvoicePay
                            />
                        </Elements>
                    )}
                </Preloader>
            </div>
        </div>
    );
};
